import type { TenantFeatureKey } from '@mfw/common';
import type React from 'react';

// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../../hooks/tenants/useTenantFeatures';
import { Navigate } from '../../router/components';

export interface TenantFeatureRouteProps {
  /** One feature, or several that must ALL be on. */
  feature: TenantFeatureKey | readonly TenantFeatureKey[];
  /**
   * Let the visitor through even with a feature off — e.g. `/premium` for an
   * account that keeps a paid entitlement. Resolve it before rendering this
   * wrapper with `true`: an `allow` that is still loading reads as "no".
   */
  allow?: boolean;
  children: React.ReactNode;
}

/**
 * The route half of the per-tenant feature switches: a switched-off discovery
 * route (a bookmark, a deep link, an old notification) goes to `/`, replacing
 * its history entry so Back does not bounce into it again.
 *
 * Renders nothing until the applicable tenant is resolved — a platform user's
 * `/auth/me` may still turn every answer to "on" (`useTenantFeatures`).
 * Platform users always pass. Permission-gated surfaces need no wrapper: the
 * same switches already reach them through `hasPermission`.
 */
const TenantFeatureRoute = ({
  allow,
  children,
  feature,
}: TenantFeatureRouteProps) => {
  const { isLoading, isOn } = useTenantFeatures();

  if (isLoading) return null;

  const features: readonly TenantFeatureKey[] =
    typeof feature === 'string' ? [feature] : feature;
  if (!allow && features.some((key) => !isOn(key))) {
    return <Navigate replace to="/" />;
  }

  return <>{children}</>;
};

export default TenantFeatureRoute;
